import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatReportDate } from '../utils/dateUtils';
import { pdf } from '@react-pdf/renderer';
import ContainerInspectionReportPDF from '../components/ContainerInspectionReportPDF';
import MigrosContainerInspectionReportPDF from '../components/MigrosContainerInspectionReportPDF';

type PdfTemplate = 'qa-mate' | 'migros';

const ContainerInspectionReportDetail: React.FC = () => {
  const { id } = useParams();
  const { token, logout } = useAuth();
  const [inspection, setInspection] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const zoomContainerRef = useRef<HTMLDivElement>(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfStatusIndex, setPdfStatusIndex] = useState(0);
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [imageStatusIndex, setImageStatusIndex] = useState(0);
  const [selectedTemplate, setSelectedTemplate] = useState<PdfTemplate>('qa-mate');
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  const openPreview = useCallback((url: string | null) => {
    setZoom(1);
    setPreviewImage(url);
  }, []);

  useEffect(() => {
    const el = zoomContainerRef.current;
    if (!el || !previewImage) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((prev: number) => Math.min(Math.max(0.25, prev - e.deltaY * 0.001), 5));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [previewImage]);

  useEffect(() => {
    const fetchInspection = async () => {
      try {
        setIsLoading(true);
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/container-inspections/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.status === 401) {
          logout();
          return;
        }

        const result = await response.json();
        if (!response.ok) throw new Error(result.message || 'Failed to fetch inspection details');

        setInspection(result.data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (id && token) fetchInspection();
  }, [id, token, logout]);

  const templateOptions: { value: PdfTemplate; name: string; icon: string; iconBg: string; iconColor: string; description: string }[] = [
    {
      value: 'qa-mate',
      name: 'QA Mate Template',
      icon: 'verified',
      iconBg: 'bg-blue-100',
      iconColor: 'text-[#136bce]',
      description: 'Default QA Mate container inspection report layout.',
    },
    {
      value: 'migros',
      name: 'Migros Template',
      icon: 'inventory_2',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      description: 'Migros Asia — Final Random Inspection (Textile/Hometextile) Version-1 layout, adapted to container inspection data.',
    },
  ];
  const selectedTemplateOption = templateOptions.find(t => t.value === selectedTemplate)!;

  const pdfStatusMessages = [
    'Preparing container inspection data…',
    'Securing report metadata…',
    'Fetching checklist evidence images…',
    'Compiling stuffing report…',
    'Embedding signatures…',
    'Rendering high-quality pages…',
    'Almost there — finalizing your PDF…',
  ];

  const imageStatusMessages = [
    'Collecting image list…',
    'Fetching stuffing images…',
    'Fetching additional images…',
    'Fetching checklist evidence…',
    'Fetching verification evidence…',
    'Compressing into ZIP archive…',
    'Almost done — finalizing your download…',
  ];

  useEffect(() => {
    if (!generatingPDF) { setPdfStatusIndex(0); return; }
    const interval = setInterval(() => setPdfStatusIndex(i => (i + 1) % pdfStatusMessages.length), 3000);
    return () => clearInterval(interval);
  }, [generatingPDF]);

  useEffect(() => {
    if (!downloadingImages) { setImageStatusIndex(0); return; }
    const interval = setInterval(() => setImageStatusIndex(i => (i + 1) % imageStatusMessages.length), 2500);
    return () => clearInterval(interval);
  }, [downloadingImages]);

  useEffect(() => {
    if (!isTemplateDropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node))
        setIsTemplateDropdownOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isTemplateDropdownOpen]);

  const toDataUrl = async (url: string): Promise<string> => {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    let resolved = url;
    if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('data:')) {
      resolved = `${baseUrl}${url}`;
    }
    if (resolved.includes('storage.googleapis.com')) {
      resolved = `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(resolved)}`;
    }
    return new Promise((resolve, reject) => {
      fetch(resolved)
        .then(r => r.blob())
        .then(blob => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
        .catch(reject);
    });
  };

  const handleGeneratePDF = async (template: PdfTemplate) => {
    if (!inspection) return;
    setPdfModalOpen(false);
    setGeneratingPDF(true);
    try {
      const cache = new Map<string, string>();
      const resolveUrl = async (url: string | null | undefined): Promise<string> => {
        if (!url) return '';
        if (cache.has(url)) return cache.get(url)!;
        try { const r = await toDataUrl(url); cache.set(url, r); return r; }
        catch { return ''; }
      };
      const resolveArr = async (urls: string[] | undefined): Promise<string[]> => {
        if (!urls || urls.length === 0) return [];
        return Promise.all(urls.map(u => resolveUrl(u)));
      };

      const clone = JSON.parse(JSON.stringify(inspection));
      clone.overall_result = getOverallStatus();

      // Resolve checklist evidence_urls
      for (const item of clone.checklists || []) {
        item.evidence_urls = await resolveArr(item.evidence_urls);
      }
      // Resolve verification evidence_urls
      for (const v of clone.verifications || []) {
        v.evidence_urls = await resolveArr(v.evidence_urls);
      }
      // Resolve photos
      for (const photo of clone.photos || []) {
        if (photo.image_url) photo.image_url = await resolveUrl(photo.image_url);
      }
      // Resolve signatures
      clone.inspector_signature_url = await resolveUrl(clone.inspector_signature_url);
      clone.factory_rep_signature_url = await resolveUrl(clone.factory_rep_signature_url);

      const docElement = template === 'migros'
        ? <MigrosContainerInspectionReportPDF inspection={clone} />
        : <ContainerInspectionReportPDF inspection={clone} />;

      const blob = await pdf(docElement).toBlob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const suffix = (inspection.container_no || inspection.id?.slice(0, 8) || 'report').replace(/\s+/g, '-').toUpperCase();
      link.download = template === 'migros'
        ? `Migros-Container-Inspection-${suffix}.pdf`
        : `Container-Inspection-${suffix}.pdf`;
      link.click();
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleDownloadImages = async () => {
    if (!inspection) return;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

    const fetchBlob = async (url: string): Promise<Blob | null> => {
      if (!url) return null;
      let abs = url;
      if (!/^(https?:|blob:|data:)/.test(abs)) abs = `${baseUrl}${abs.startsWith('/') ? '' : '/'}${abs}`;
      const isGcs = abs.includes('storage.googleapis.com');
      const fetchUrl = isGcs ? `${baseUrl}/api/v1/proxy-image?url=${encodeURIComponent(abs)}` : abs;
      try {
        const res = await fetch(fetchUrl, { mode: 'cors' });
        if (res.ok) return await res.blob();
      } catch {}
      return null;
    };

    const getExt = (url: string, blob: Blob) => {
      const m = url.match(/\.(\w{2,5})(\?|$)/);
      if (m) return m[1].toLowerCase();
      if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
      if (blob.type.includes('png')) return 'png';
      if (blob.type.includes('webp')) return 'webp';
      return 'jpg';
    };

    const stuffingImgs = (inspection.photos || []).filter((p: any) => p.is_required !== false && p.image_url);
    const additionalImgs = (inspection.photos || []).filter((p: any) => p.is_required === false && p.image_url);
    const checklistImgs = (inspection.checklists || []).flatMap((c: any) => (c.evidence_urls || []).filter(Boolean));
    const verificationImgs = (inspection.verifications || []).flatMap((v: any) => (v.evidence_urls || []).filter(Boolean));

    const total = stuffingImgs.length + additionalImgs.length + checklistImgs.length + verificationImgs.length;
    if (total === 0) { alert('No images found to download.'); return; }

    setDownloadingImages(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      if (stuffingImgs.length > 0) {
        const folder = zip.folder('Stuffing_Images')!;
        for (let i = 0; i < stuffingImgs.length; i++) {
          const blob = await fetchBlob(stuffingImgs[i].image_url);
          if (blob) {
            const ext = getExt(stuffingImgs[i].image_url, blob);
            const lbl = (stuffingImgs[i].label || `Image_${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');
            folder.file(`${String(i + 1).padStart(2, '0')}_${lbl}.${ext}`, blob);
          }
        }
      }
      if (additionalImgs.length > 0) {
        const folder = zip.folder('Additional_Images')!;
        for (let i = 0; i < additionalImgs.length; i++) {
          const blob = await fetchBlob(additionalImgs[i].image_url);
          if (blob) {
            const ext = getExt(additionalImgs[i].image_url, blob);
            const lbl = (additionalImgs[i].label || `Image_${i + 1}`).replace(/[^a-zA-Z0-9_\-]/g, '_');
            folder.file(`${String(i + 1).padStart(2, '0')}_${lbl}.${ext}`, blob);
          }
        }
      }
      if (checklistImgs.length > 0) {
        const folder = zip.folder('Checklist_Evidence')!;
        for (let i = 0; i < checklistImgs.length; i++) {
          const blob = await fetchBlob(checklistImgs[i]);
          if (blob) folder.file(`${String(i + 1).padStart(2, '0')}_Checklist.${getExt(checklistImgs[i], blob)}`, blob);
        }
      }
      if (verificationImgs.length > 0) {
        const folder = zip.folder('Verification_Evidence')!;
        for (let i = 0; i < verificationImgs.length; i++) {
          const blob = await fetchBlob(verificationImgs[i]);
          if (blob) folder.file(`${String(i + 1).padStart(2, '0')}_Verification.${getExt(verificationImgs[i], blob)}`, blob);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const suffix = (inspection.container_no || inspection.id?.slice(0, 8) || 'report').replace(/\s+/g, '-').toUpperCase();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(zipBlob);
      a.download = `Container-Inspection-Images-${suffix}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: any) {
      console.error('Image download failed:', err);
      alert(`Image download failed: ${err?.message || 'Unknown error'}`);
    } finally {
      setDownloadingImages(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-slate-500">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600 mb-4"></div>
        <p className="font-medium">Loading report details...</p>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-red-500 p-8 text-center">
        <span className="material-symbols-outlined text-6xl mb-4">error</span>
        <h2 className="text-xl font-bold mb-2">Error Loading Report</h2>
        <p className="max-w-md mb-6">{error || 'Inspection not found'}</p>
        <Link to="/container-inspection-reports" className="px-6 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors font-bold">
          Back to Reports
        </Link>
      </div>
    );
  }

  const handleDownload = () => {
    window.print();
  };

  const getPassedCount = () => {
    return inspection.checklists?.filter((c: any) => c.status === 'pass').length || 0;
  };

  const getTotalCount = () => {
    return inspection.checklists?.length || 0;
  };

  const getVerificationStatus = (label: string) => {
    const v = inspection.verifications?.find((ver: any) => ver.title === label);
    return v ? (v.status === 'pass' ? 'Passed' : 'Failed') : 'N/A';
  };

  const getOverallStatus = () => {
    if (inspection.status !== 'COMPLETED') return 'ACTIVE';
    
    // Check if any checklist item is failed
    const hasChecklistFail = inspection.checklists?.some((item: any) => 
      (item.status ?? '').toString().toLowerCase() === 'fail'
    );
    
    // Check if any verification item is failed
    const hasVerificationFail = inspection.verifications?.some((item: any) => 
      (item.status ?? '').toString().toLowerCase() === 'fail'
    );
    
    if (hasChecklistFail || hasVerificationFail) {
      return 'FAIL';
    }
    
    return 'PASS';
  };

  const resolvePhotoUrl = (url: string | null | undefined): string | null => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:') || url.startsWith('data:')) return url;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
    return `${baseUrl}${url}`;
  };

  const getIconForTitle = (title: string) => {
    const icons: { [key: string]: string } = {
      'Understructure': 'foundation',
      'Side Walls (Left)': 'border_left',
      'Side Walls (Right)': 'border_right',
      'Front Wall': 'check_box_outline_blank',
      'Ceiling/Roof': 'roofing',
      'Doors & Hinges': 'door_front',
      'Floor Status': 'floor',
      'Locking Mechanism': 'lock',
      'Door Seals': 'approval',
      'Exterior (Right)': 'view_sidebar',
      'Exterior (Left)': 'view_sidebar',
      'Exterior Front': 'crop_portrait',
      'Exterior Roof': 'deck',
      'Cleanliness': 'clean_hands',
      'Odor Check': 'fragrance',
      'Pest Control': 'pest_control',
      'CSC Plate': 'badge'
    };
    return icons[title] || 'check_circle';
  };

  const checklistItems = inspection.checklists?.map((item: any) => {
    const s = (item.status ?? '').toString().toLowerCase();
    let status: string;
    if (s === 'pass') status = 'Passed';
    else if (s === 'fail') status = 'Failed';
    else status = 'Skipped'; // null, '', 'skip', 'skipped', or any other value

    return {
      title: item.title,
      desc: item.description,
      icon: getIconForTitle(item.title),
      status: status,
      remark: item.remarks || 'No remarks provided.',
      thumbnail: resolvePhotoUrl(item.evidence_urls?.[0])
    };
  }) || [];

  const stuffingImages = inspection.photos
    ?.filter((p: any) => p.is_required !== false)
    .map((p: any) => ({
      label: p.label || 'Stuffing Image',
      icon: 'image',
      url: resolvePhotoUrl(p.image_url)
    })) || [];

  const additionalImages = inspection.photos
    ?.filter((p: any) => p.is_required === false)
    .map((p: any) => ({
      label: p.label || 'Additional Image',
      url: resolvePhotoUrl(p.image_url)
    })) || [];

  const finalVerifications = inspection.verifications?.map((v: any) => ({
    label: v.title,
    icon: 'verified_user',
    status: v.status,
    remark: v.remarks || 'No remarks.',
    thumbnail: resolvePhotoUrl(v.evidence_urls?.[0])
  })) || [];

  const bundleStats = {
    total: inspection.bundle_quantity || 0,
    checked: inspection.bundles?.filter((b: any) => b.is_checked === true).length || 0,
    shutout: inspection.bundles?.filter((b: any) => b.is_checked === false).length || 0
  };

  return (
    <div className="mx-auto w-full max-w-7xl pb-12 animate-in fade-in duration-500 print:pb-0 print:max-w-none print:w-full print:animate-none space-y-8">
      {/* Breadcrumbs - Hidden in Print */}
      <nav aria-label="Breadcrumb" className="flex mb-6 text-sm text-slate-500 print:hidden">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900 transition-colors">
              <span className="material-symbols-outlined text-[18px] mr-2">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <Link to="/container-inspection-reports" className="ml-1 hover:text-slate-900 md:ml-2 transition-colors">Container Reports</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-slate-400 text-[18px]">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">#{id || 'MSKU-1234567'}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header section */}
      <div className="flex flex-col items-start sm:flex-row sm:items-center justify-between gap-4 print:mb-12">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight leading-tight">Container Inspection Report</h1>
          <p className="text-sm text-slate-500 mt-1">Detailed breakdown of loading inspection outcomes</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto print:hidden">
          <button
            onClick={() => setPdfModalOpen(true)}
            disabled={generatingPDF}
            className="flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex-1 sm:flex-none"
          >
            {generatingPDF ? (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <span className="material-symbols-outlined text-[20px]">picture_as_pdf</span>
            )}
            {generatingPDF ? 'Generating...' : 'Generate PDF'}
          </button>
          <button
            onClick={handleDownloadImages}
            disabled={downloadingImages}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-violet-600 hover:bg-violet-700 active:scale-95 transition-all flex-1 sm:flex-none disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">{downloadingImages ? 'hourglass_top' : 'photo_library'}</span>
            {downloadingImages ? 'Zipping…' : 'Download Images'}
          </button>
          <button
            onClick={handleDownload}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg shadow-md text-sm font-bold text-white bg-[#136bce] hover:bg-[#0e52a0] active:scale-95 transition-all flex-1 sm:flex-none"
          >
            <span className="material-symbols-outlined text-[20px]">download</span>
            Download Report
          </button>
        </div>
      </div>

      {/* Standardized Result Status Banner */}
      <div className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden flex flex-col md:flex-row h-auto md:h-44">
        <div className={`md:w-1/3 text-white p-6 flex flex-col items-center justify-center text-center relative overflow-hidden ${inspection.status === 'COMPLETED' ? (getOverallStatus() === 'PASS' ? 'bg-emerald-500' : 'bg-rose-500') : 'bg-amber-500'}`}>
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-100 z-0"></div>
          <span className="material-symbols-outlined absolute -right-6 -bottom-6 text-[140px] opacity-20 rotate-12 select-none z-0">
            {inspection.status === 'COMPLETED' ? (getOverallStatus() === 'PASS' ? 'verified' : 'cancel') : 'pending'}
          </span>
          <div className="relative z-10 w-full flex flex-col items-center justify-center h-full">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase mb-2 shadow-sm ring-1 ring-white/30">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              {inspection.status === 'COMPLETED' ? 'Inspection Complete' : 'In Progress'}
            </div>
            <h2 className="text-4xl font-black tracking-tighter drop-shadow-sm leading-none mb-1">
              {getOverallStatus()}
            </h2>
            <p className="text-white/80 text-[10px] font-medium opacity-90 uppercase tracking-widest">
              Status: {inspection.status}
            </p>
          </div>
        </div>
        <div className="md:w-2/3 p-6 flex items-center justify-around bg-white relative">
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Checklist Score</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-[#136bce] transition-colors">
                {getPassedCount()}/{getTotalCount()}
              </p>
              <span className="text-sm font-bold text-slate-400">passed</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Bundles</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-emerald-500 transition-colors">
                {bundleStats.checked}
              </p>
              <span className="text-sm font-bold text-slate-400">/{bundleStats.total}</span>
            </div>
          </div>
          <div className="w-px h-20 bg-slate-100 mx-4"></div>
          <div className="text-center group">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Shutout</p>
            <div className="flex items-baseline justify-center gap-2">
              <p className="text-4xl font-black text-slate-800 group-hover:text-rose-500 transition-colors">
                {bundleStats.shutout}
              </p>
              <span className="text-[10px] font-bold text-slate-400 uppercase">Items</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8 print:space-y-12">
        {/* Summary Details */}
        <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm print:shadow-none print:border-slate-300">
          <h3 className="text-base font-bold text-slate-800 mb-6 pb-4 border-b border-slate-100 flex items-center gap-2">
            <span className="material-symbols-outlined text-[#136bce] text-[22px]">info</span>
            Summary Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-6 print:grid-cols-2 print:gap-y-10">
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Container Number</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.container_no}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Buyer Name</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.buyer_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">PO Number</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.po_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Vehicle No.</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.vehicle_no || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Sealing No.</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.seal_no || 'N/A'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Invoice Number</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.invoice_no || 'N/A'}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Inspection Date & Time</p>
              <p className="text-base font-semibold text-slate-900 mt-1">
                {formatReportDate(inspection.created_at)}
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Bundle Quantity</p>
              <p className="text-base font-semibold text-slate-900 mt-1">{inspection.bundle_quantity} Bundles</p>
            </div>
            <div className="sm:col-span-1 print:col-span-1">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Shipping Line</p>
              <p className="text-base font-semibold text-slate-900 mt-1 flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-800 text-[20px] print:text-blue-900">sailing</span>
                {inspection.shipping_line || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* 17-Point Checklist */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden break-inside-avoid print:border-slate-300">
          <div className="px-8 py-5 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between print:bg-slate-50">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">fact_check</span>
              17-Point Checklist
            </h3>
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider print:text-slate-500">Score: {getPassedCount()}/{getTotalCount()}</span>
              <span className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-[10px] font-bold border uppercase tracking-wide print:bg-emerald-50 print:text-emerald-700 print:border-emerald-300 ${getPassedCount() === getTotalCount() ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>
                {getPassedCount() === getTotalCount() ? 'PASS' : 'PARTIAL'}
              </span>
            </div>
          </div>
          <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10 print:grid-cols-2 print:gap-8">
            {checklistItems.map((item: any, idx: number) => (
              <div key={idx} className={`flex flex-col gap-4 p-6 rounded-xl border transition-all shadow-sm hover:shadow-md print:shadow-none print:break-inside-avoid ${item.status === 'Passed'
                ? 'border-slate-100 bg-white hover:border-slate-300 print:border-slate-200'
                : item.status === 'Skipped'
                  ? 'border-slate-200 bg-slate-50/80 print:border-slate-300 print:bg-slate-50'
                  : 'border-red-100 bg-red-50/50 print:border-red-400 print:bg-red-50'
                }`}>
                <div className="flex items-start gap-4">
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${item.status === 'Passed'
                    ? 'bg-emerald-100 text-emerald-600 print:bg-emerald-100'
                    : item.status === 'Skipped'
                      ? 'bg-slate-200 text-slate-400 print:bg-slate-300'
                      : 'bg-red-100 text-red-500 print:bg-red-200'
                    }`}>
                    <span className="material-symbols-outlined text-[24px]">{item.icon}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-bold text-slate-900">{item.title}</h4>
                      <span className={`material-symbols-outlined text-[20px] ${item.status === 'Passed'
                        ? 'text-emerald-500'
                        : item.status === 'Skipped'
                          ? 'text-slate-400'
                          : 'text-red-500'
                        }`}>
                        {item.status === 'Passed' ? 'check_circle' : item.status === 'Skipped' ? 'help' : 'cancel'}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 font-medium print:text-slate-600">{item.desc}</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  {item.thumbnail ? (
                    <div
                      className="group relative h-24 w-24 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 shrink-0 cursor-zoom-in shadow-sm hover:ring-2 ring-[#136bce] transition-all"
                      onClick={() => openPreview(item.thumbnail)}
                    >
                      <img alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" src={item.thumbnail} />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                    </div>
                  ) : (
                    <div className="h-24 w-24 rounded-lg border border-slate-200 bg-slate-100 shrink-0 flex items-center justify-center">
                      <span className="material-symbols-outlined text-slate-300 text-[32px]">image_not_supported</span>
                    </div>
                  )}
                  <div className="flex-1 bg-slate-50/80 rounded-lg p-3 border border-slate-100 print:bg-slate-100">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Remark</span>
                    <p className="text-[11px] text-slate-600 font-medium leading-relaxed italic">"{item.remark}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stuffing Images */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 break-inside-avoid print:border-slate-300">
          <h3 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
            <span className="material-symbols-outlined text-[#136bce] text-[22px]">image</span>
            Stuffing Images
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 print:grid-cols-4 print:gap-6 mt-6">
            {stuffingImages.map((img: any, idx: number) => (
              <div key={idx} className="flex flex-col gap-2 print:break-inside-avoid">
                <div
                  className="group relative aspect-square rounded-xl bg-slate-50 overflow-hidden border border-slate-200 cursor-zoom-in shadow-sm hover:shadow-md transition-all print:border-slate-300 print:bg-slate-100"
                  onClick={() => openPreview(img.url)}
                >
                  <img src={img.url} alt={img.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                </div>
                <div className="text-center">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter print:text-slate-700">{img.label}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Images Section */}
          {additionalImages.length > 0 && (
            <div className="mt-12 pt-8 border-t border-slate-100">
              <h3 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2">
                <span className="material-symbols-outlined text-[#136bce] text-[22px]">photo_library</span>
                Additional Images
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 print:grid-cols-4 print:gap-6">
                {additionalImages.map((img: any, idx: number) => (
                  <div key={idx} className="flex flex-col gap-2 print:break-inside-avoid">
                    <div
                      className="group relative aspect-square rounded-xl bg-slate-50 overflow-hidden border border-slate-200 cursor-zoom-in shadow-sm hover:shadow-md transition-all print:border-slate-300 print:bg-slate-100"
                      onClick={() => openPreview(img.url)}
                    >
                      <img src={img.url} alt={img.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                    </div>
                    <div className="text-center">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter print:text-slate-700">{img.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Final Verification */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden break-inside-avoid print:border-slate-300">
          <div className="px-8 py-5 border-b border-slate-100 bg-slate-50/50 print:bg-slate-50">
            <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">verified_user</span>
              Final Verification
            </h3>
          </div>
          <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10 print:grid-cols-2 print:gap-8">
            {finalVerifications.map((item: any, idx: number) => (
              <div key={idx} className={`flex flex-col gap-4 p-6 rounded-xl border transition-all shadow-sm hover:shadow-md print:shadow-none print:break-inside-avoid ${item.status === 'fail' ? 'border-red-100 bg-red-50/50 print:border-red-400 print:bg-red-50' : 'border-slate-100 bg-white hover:border-slate-300 print:border-slate-200'}`}>
                <div className="flex items-start gap-4">
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${item.status === 'fail' ? 'bg-red-100 text-red-500 print:bg-red-200' : 'bg-blue-50 text-[#136bce] print:bg-blue-100'}`}>
                    <span className="material-symbols-outlined text-[24px]">{item.icon}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-bold text-slate-900">{item.label}</h4>
                      <span className={`material-symbols-outlined text-[20px] ${item.status === 'fail' ? 'text-red-500' : 'text-emerald-500'}`}>
                        {item.status === 'pass' ? 'check_circle' : 'cancel'}
                      </span>
                    </div>
                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${item.status === 'pass' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-red-50 text-red-700 border-red-100'}`}>
                      {item.status}
                    </span>
                  </div>
                </div>

                <div className="flex gap-4">
                  {item.thumbnail ? (
                    <div
                      className="group relative h-24 w-24 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 shrink-0 cursor-zoom-in shadow-sm hover:ring-2 ring-[#136bce] transition-all"
                      onClick={() => openPreview(item.thumbnail)}
                    >
                      <img alt={item.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" src={item.thumbnail} />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                    </div>
                  ) : (
                    <div className="h-24 w-24 rounded-lg border border-slate-200 bg-slate-100 shrink-0 flex items-center justify-center">
                      <span className="material-symbols-outlined text-slate-300 text-[32px]">image_not_supported</span>
                    </div>
                  )}
                  <div className="flex-1 bg-slate-50/80 rounded-lg p-3 border border-slate-100 print:bg-slate-100">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Inspector Remark</span>
                    <p className="text-[11px] text-slate-600 font-medium leading-relaxed italic">"{item.remark}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bundle Checkout Report & Grid View */}
        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 break-inside-avoid print:border-slate-300">
          <h3 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
            <span className="material-symbols-outlined text-[#136bce] text-[22px]">inventory_2</span>
            Bundle Checkout Report
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Total Bundles</p>
              <p className="text-4xl font-black text-slate-800">{bundleStats.total}</p>
            </div>
            <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm">
              <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-2">Checked</p>
              <p className="text-4xl font-black text-emerald-700">{bundleStats.checked}</p>
            </div>
            <div className="p-6 bg-rose-50 rounded-2xl border border-rose-100 shadow-sm">
              <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest mb-2">Shutout</p>
              <p className="text-4xl font-black text-rose-700">{bundleStats.shutout}</p>
            </div>
          </div>

          <div className="mt-12">
            <h4 className="text-sm font-bold text-slate-800 mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-400 text-[20px]">grid_view</span>
              Bundle Grid View
            </h4>
            <div className="flex flex-wrap gap-x-3 gap-y-5">
              {inspection.bundles?.map((bundle: any, i: number) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold transition-all border shadow-sm ${bundle.is_checked
                        ? 'bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600'
                        : 'bg-rose-100 text-rose-600 border-rose-200 hover:bg-rose-200'
                      }`}
                    title={`Bundle #${bundle.bundle_index}: ${bundle.is_checked ? 'CHECKED' : 'SHUTOUT'}`}
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      {bundle.is_checked ? 'check' : 'close'}
                    </span>
                  </div>
                  <span className="text-[9px] font-bold text-slate-400">{bundle.bundle_index}</span>
                </div>
              ))}
              {(!inspection.bundles || inspection.bundles.length === 0) && (
                <p className="text-slate-400 text-xs italic">No bundle data scanned for this report.</p>
              )}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-8 text-[11px] font-bold uppercase tracking-widest text-slate-400">
              <div className="flex items-center gap-2.5">
                <span className="w-4 h-4 rounded-md bg-emerald-500 border border-emerald-600"></span>
                <span className="text-slate-600">Checked Items ({bundleStats.checked})</span>
              </div>
              <div className="flex items-center gap-2.5">
                <span className="w-4 h-4 rounded-md bg-rose-100 border border-rose-200"></span>
                <span className="text-slate-600">Shutout Items ({bundleStats.shutout})</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 gap-8 pb-8 break-inside-avoid print:pb-4 print:gap-10">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col print:border-slate-300 print:p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">notes</span>
              Additional Notes / Remarks
            </h3>
            <div className="flex-1 bg-slate-50 rounded-xl border border-slate-200 p-4 shadow-inner print:bg-slate-100 print:border-slate-300 print:shadow-none">
              <textarea
                className="w-full h-full min-h-[140px] bg-transparent border-0 focus:ring-0 text-slate-600 placeholder-slate-400 resize-none text-sm font-medium leading-relaxed print:text-slate-800"
                placeholder="No final observations provided."
                readOnly
                value={inspection.remarks || 'No additional notes provided by the inspector.'}
              />
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-12 break-inside-avoid print:pb-0 print:gap-10">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col print:border-slate-300 print:p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">person</span>
              Factory Rep Signature
            </h3>
            <div className="flex-1 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center min-h-[160px] relative group overflow-hidden hover:bg-slate-100 hover:border-[#136bce]/30 transition-all print:border-slate-300 print:bg-slate-100">
              <div className="relative z-10 flex flex-col items-center">
                {inspection.factory_rep_signature_url ? (
                  <img
                    alt="Factory Rep Signature"
                    className="h-24 object-contain opacity-90 transition-transform group-hover:scale-105"
                    src={inspection.factory_rep_signature_url}
                  />
                ) : (
                  <div className="text-slate-400 text-xs italic">No signature captured</div>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8 flex flex-col print:border-slate-300 print:p-6">
            <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-[#136bce]">draw</span>
              Inspector Signature
            </h3>
            <div className="flex-1 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center min-h-[160px] relative group overflow-hidden hover:bg-slate-100 hover:border-[#136bce]/30 transition-all print:border-slate-300 print:bg-slate-100">
              <div className="relative z-10 flex flex-col items-center">
                {inspection.inspector_signature_url ? (
                  <img
                    alt="Inspector Signature"
                    className="h-24 object-contain opacity-90 transition-transform group-hover:scale-105"
                    src={inspection.inspector_signature_url}
                  />
                ) : (
                  <div className="text-slate-400 text-xs italic">No signature captured</div>
                )}
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-2">
                  {inspection.inspector?.name || inspection.inspector_name || 'Authorized Inspector'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end text-[11px] text-slate-400 font-bold px-2 tracking-wide uppercase print:mt-20 print:border-t print:pt-6 print:border-slate-200">
        <span className="hidden print:block text-slate-300">System-Generated Document</span>
      </div>

      {/* PDF Generation Modal — premium animated */}
      {generatingPDF && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 via-blue-500 to-emerald-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-emerald-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center shadow-lg shadow-emerald-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">picture_as_pdf</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-emerald-500 shadow-md shadow-emerald-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Generating your PDF</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                We're crafting a beautifully formatted container inspection report. This usually takes just a few seconds.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </div>
                  <p key={pdfStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {pdfStatusMessages[pdfStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-emerald-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── Download Images Modal — premium animated ───── */}
      {downloadingImages && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4 animate-in fade-in duration-300 print:hidden">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-400 via-fuchsia-500 to-violet-400 bg-[length:200%_100%] animate-gradient-x" />
            <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-violet-300/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-fuchsia-300/30 blur-3xl" />
            <div className="relative p-8 flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-500 blur-xl opacity-50 animate-pulse" />
                <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-500/40">
                  <span className="material-symbols-outlined text-white text-[40px] animate-pulse">photo_library</span>
                </div>
                <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-3 w-3 rounded-full bg-violet-500 shadow-md shadow-violet-500/50" />
                </div>
              </div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-2">Downloading Images</h2>
              <p className="text-sm text-slate-500 font-medium leading-relaxed mb-6 max-w-xs">
                Fetching and packaging all report images into a neatly organised ZIP folder.
              </p>
              <div className="w-full bg-slate-50 rounded-xl border border-slate-100 px-4 py-3 mb-6 overflow-hidden">
                <div className="flex items-center gap-3">
                  <div className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-500" />
                  </div>
                  <p key={imageStatusIndex} className="text-sm font-bold text-slate-700 text-left flex-1 animate-in fade-in slide-in-from-bottom-1 duration-500">
                    {imageStatusMessages[imageStatusIndex]}
                  </p>
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full w-1/3 bg-gradient-to-r from-violet-500 to-fuchsia-500 rounded-full animate-pdf-progress" />
              </div>
              <p className="mt-6 text-[10px] uppercase tracking-widest font-bold text-slate-400">
                Powered by <span className="text-violet-600">QA Mate</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ───── PDF Template Selector Modal ───── */}
      {pdfModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4 print:hidden"
          onClick={() => !generatingPDF && setPdfModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-600 text-[22px]">picture_as_pdf</span>
                <h3 className="text-base font-bold text-slate-900">Generate PDF Report</h3>
              </div>
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-30"
              >
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Template</label>
                <div className="relative" ref={templateDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsTemplateDropdownOpen(prev => !prev)}
                    disabled={generatingPDF}
                    className="w-full flex items-center gap-2 rounded-lg border border-slate-200 bg-white py-2.5 pl-3 pr-9 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <span className={`h-6 w-6 rounded-full ${selectedTemplateOption.iconBg} flex items-center justify-center shrink-0`}>
                      <span className={`material-symbols-outlined text-[14px] ${selectedTemplateOption.iconColor}`}>
                        {selectedTemplateOption.icon}
                      </span>
                    </span>
                    <span className="truncate">{selectedTemplateOption.name}</span>
                    <span className="material-symbols-outlined pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">expand_more</span>
                  </button>
                  {isTemplateDropdownOpen && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-xl border border-slate-200 bg-white shadow-lg py-1 overflow-hidden">
                      {templateOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setSelectedTemplate(opt.value); setIsTemplateDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-blue-50 transition-colors text-left ${selectedTemplate === opt.value ? 'bg-blue-50 text-[#136bce] font-semibold' : 'text-slate-700'}`}
                        >
                          <span className={`h-6 w-6 rounded-full ${opt.iconBg} flex items-center justify-center shrink-0`}>
                            <span className={`material-symbols-outlined text-[14px] ${opt.iconColor}`}>{opt.icon}</span>
                          </span>
                          {opt.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2 leading-snug">{selectedTemplateOption.description}</p>
              </div>

              {generatingPDF && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-[18px] animate-spin">progress_activity</span>
                  <span className="text-xs font-medium text-emerald-800">{pdfStatusMessages[pdfStatusIndex]}</span>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
              <button
                onClick={() => setPdfModalOpen(false)}
                disabled={generatingPDF}
                className="rounded-lg px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-200 transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleGeneratePDF(selectedTemplate);
                  setPdfModalOpen(false);
                }}
                disabled={generatingPDF}
                className="flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-bold text-white shadow-md shadow-emerald-500/30 hover:bg-emerald-700 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-[18px]">
                  {generatingPDF ? 'hourglass_top' : 'picture_as_pdf'}
                </span>
                {generatingPDF ? 'Generating…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-sm p-4 animate-in fade-in duration-200 print:hidden"
          onClick={() => { setPreviewImage(null); setZoom(1); }}
        >
          {/* Close */}
          <button
            className="absolute top-6 right-6 text-white hover:text-slate-300 transition-colors p-2 rounded-full bg-slate-800/50 z-10"
            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); setZoom(1); }}
          >
            <span className="material-symbols-outlined text-[32px]">close</span>
          </button>

          {/* Zoom controls */}
          <div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-slate-900/80 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.max(0.25, parseFloat((prev - 0.25).toFixed(2))))}
              disabled={zoom <= 0.25}
            >
              <span className="material-symbols-outlined text-[22px]">remove</span>
            </button>
            <span className="text-white text-xs font-bold tracking-widest w-12 text-center select-none">
              {Math.round(zoom * 100)}%
            </span>
            <button
              className="text-white/70 hover:text-white transition-colors disabled:opacity-30"
              onClick={() => setZoom((prev: number) => Math.min(5, parseFloat((prev + 0.25).toFixed(2))))}
              disabled={zoom >= 5}
            >
              <span className="material-symbols-outlined text-[22px]">add</span>
            </button>
            <div className="w-px h-4 bg-white/20 mx-1" />
            <button
              className="text-white/70 hover:text-white transition-colors"
              onClick={() => setZoom(1)}
            >
              <span className="material-symbols-outlined text-[20px]">fit_screen</span>
            </button>
          </div>

          {/* Image container — wheel listener attached via ref */}
          <div
            ref={zoomContainerRef}
            className="flex items-center justify-center overflow-hidden max-h-[90vh] max-w-[90vw]"
            style={{ cursor: zoom > 1 ? 'grab' : 'zoom-in' }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={previewImage}
              alt="Preview"
              className="rounded-lg shadow-2xl animate-in zoom-in-95 duration-300 max-h-[82vh] max-w-[88vw] object-contain"
              style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 0.1s ease' }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ContainerInspectionReportDetail;
